<template>
  <article class="support">
    <TextBanner>
      <h1>Contact Us</h1>
      <p>We would love to hear from you.</p>
    </TextBanner>

    <div class="grid grid--container support__inner">
      <div class="row row--lg-h-center">
        <div class="col col--lg-7 col--md-9">
          <p>
            Be sure to check our
            <a href="https://support.campsite.bio/en/">support</a> page if you
            have questions about your profile or using Campsite.bio.
          </p>

          <div class="row">
            <div class="col col--xl-7 col--lg-8 col--sm-9">
              <p class="error" v-if="errorMessage" aria-live="assertive">
                {{ errorMessage }}
              </p>
              <p class="success" v-if="successMessage" aria-live="polite">
                {{ successMessage }}
              </p>
              <form
                action="#"
                method="post"
                @submit.prevent="validateForm"
                ref="form"
                v-else
              >
                <FormGroup>
                  <Label for="reason">Select a Reason</Label>
                  <Select
                    id="reason"
                    name="reason"
                    required
                    v-model="reason"
                    @focus="handleFocus"
                  >
                    <option value="Support">Support</option>
                    <option value="Issues/Bugs">Issues/Bugs</option>
                    <option value="Custom Plans">Custom Plans</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label for="name">Your Name</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    name="full_name"
                    required
                    v-model="name"
                    @focus="handleFocus"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="email">Your Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="johnsmith@campsite.bio"
                    required
                    v-model="email"
                    @focus="handleFocus"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="message">Message</Label>
                  <Input
                    id="message"
                    name="message"
                    component="textarea"
                    required
                    rows="4"
                    v-model="message"
                    @focus="handleFocus"
                  />
                </FormGroup>

                <div class="hide">
                  <input
                    type="text"
                    name="phone"
                    autocomplete="off"
                    v-model="phone"
                  />
                  <input type="hidden" name="focus" v-model="hasFocused" />
                  <input type="hidden" name="token" v-model="captcha" />
                </div>

                <FormActions>
                  <VueHcaptcha
                    ref="invisibleRecaptcha"
                    sitekey="5bbad349-e41e-4564-b8da-6dde650962dc"
                    @verify="captchaResponse"
                    @expired="captcha = ''"
                    size="invisible"
                  />
                  <Button type="submit" :loading="isSending || isCaptching"
                    >Send</Button
                  >
                </FormActions>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import VueHcaptcha from '@hcaptcha/vue-hcaptcha';
import {
  FormGroup,
  Label,
  Input,
  Button,
  Select,
} from '@campsite-bio/component-lib';
import { nextTick, ref, useContext } from '@nuxtjs/composition-api';

import defaultOG from '@/assets/default-og';
import TextBanner from '@/components/text-banner';
import FormActions from '@/components/form-actions';

export default {
  head: {
    title: 'Contact Campsite',
    meta: [
      ...defaultOG,
      {
        hid: 'description',
        name: 'description',
        content: 'Get in touch with someone at Campsite.',
      },
    ],
  },

  components: {
    TextBanner,
    VueHcaptcha,
    Input,
    FormActions,
    Label,
    FormGroup,
    Button,
    Select,
  },

  setup() {
    const reason = ref('Support');
    const email = ref(null);
    const name = ref(null);
    const message = ref(null);
    const phone = ref(null);
    const successMessage = ref(null);
    const errorMessage = ref(null);
    const invisibleRecaptcha = ref(null);
    const form = ref(null);
    const captcha = ref(false);
    const isCaptching = ref(false);
    const isSending = ref(false);
    const hasFocused = ref(false);

    const { $axios, $sentryReady, isDev, $config } = useContext();

    function handleFocus() {
      hasFocused.value = true;
    }

    async function captchaResponse(response) {
      isCaptching.value = false;
      captcha.value = response;
      await nextTick();
      submit();
    }

    function validateForm() {
      if (isDev) {
        submit();
        return;
      }

      if (!hasFocused.value || isCaptching.value) return;

      // verify human
      invisibleRecaptcha.value.execute();
      isCaptching.value = true;
    }

    async function submit() {
      errorMessage.value = null;
      if (isSending.value) return;

      isSending.value = true;
      const formData = new FormData(form.value);

      try {
        const { data } = await $axios.request({
          method: 'post',
          url: `${$config.apiBaseUrl}/marketing/contact`,
          responseType: 'json',
          data: formData,
        });

        successMessage.value = data.message;

        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      } catch (e) {
        invisibleRecaptcha.value.reset();
        console.error(e);
        errorMessage.value =
          'There was an error when trying to contact us. Please try again soon.';
        // $sentry from setup is the lazy-load mock, which throws once the SDK has
        // loaded; $sentryReady resolves with the loaded SDK (CAM-673).
        $sentryReady().then((sentry) => sentry.captureException(e));
      } finally {
        isSending.value = false;
      }
    }

    return {
      reason,
      email,
      name,
      message,
      phone,
      successMessage,
      errorMessage,
      invisibleRecaptcha,
      captcha,
      isCaptching,
      isSending,
      hasFocused,
      form,
      handleFocus,
      validateForm,
      captchaResponse,
    };
  },
};
</script>

<style lang="scss" scoped>
.success {
  color: var(--g-color-success);
  font-weight: 700;
}

.error {
  color: var(--g-color-error);
  font-weight: 700;
}
</style>
